/** @jsx jsx */
import React, { memo } from 'react';
import { css, styled, jsx } from '@compiled/react';
import noop from 'lodash/noop';
// eslint-disable-next-line @atlaskit/design-system/no-unsupported-drag-and-drop-libraries
import type { DraggableProvidedDragHandleProps } from 'react-beautiful-dnd';
import DragHandlerIcon from '@atlaskit/icon/core/drag-handle';
import CrossIcon from '@atlaskit/icon/glyph/cross';
import { Box, xcss } from '@atlaskit/primitives';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
// eslint-disable-next-line import/order
import Tooltip from '@atlaskit/tooltip';

import { gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { useAnalyticsEvents, fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { messages as dragIndicatorMessages } from '../../../../common/messages';
import { TooltipTrigger, DragProxy } from '../../../../common/ui/mouse-activity-proxy/index.tsx';
import ButtonProxy from '../../../../common/ui/mouse-activity-proxy/main.tsx';
import UrlIcon from '../../../../common/ui/url-icon/index.tsx';
import messages from './messages';

type Props = {
	id: number;
	name?: string;
	iconUrl?: string;
	dragHandleProps?: DraggableProvidedDragHandleProps | null;
	onRemove?: (arg1: number) => void;
};

const TicketTypeItem = memo<Props>(
	({ id, iconUrl = '', name = '', dragHandleProps, onRemove = noop }: Props) => {
		const { formatMessage } = useIntl();
		const { createAnalyticsEvent } = useAnalyticsEvents();

		const ariaPropsForDragButton = {
			'aria-labelledby': undefined,
			'aria-label': formatMessage(dragIndicatorMessages.draggableItemIndicatorAccessibilityLabel),
		};

		const dragAreaProps = { ...dragHandleProps, ...ariaPropsForDragButton };

		return (
			<Wrapper>
				<DragArea {...dragAreaProps}>
					<DragHandlerIcon
						label={formatMessage(dragIndicatorMessages.draggableItemIndicatorAccessibilityLabel)}
						color="currentColor"
						spacing="spacious"
					/>
					<Box xcss={iconWrapperStyles}>
						<UrlIcon url={iconUrl} alt="" />
					</Box>
					<Title>{name}</Title>
				</DragArea>
				<RemoveButton
					data-component-selector="portal-settings-request-groups.portal-group.ticket-type-list.ticket-type-item.remove-button"
					data-test-id="servicedesk-portal-settings-request-groups.ui.portal-group.ticket-type-list.ticket-type-item.remove-button"
					onClick={() => {
						fireUIAnalytics(createAnalyticsEvent({}), 'button clicked', 'requestFormRemove');
						onRemove(id);
					}}
					aria-label={formatMessage(messages.removeButton, { formName: name })}
				>
					<Tooltip content={formatMessage(messages.removeIconTooltip)}>
						<TooltipTrigger tabIndex={-1} />
					</Tooltip>
					<CrossIcon label="" />
				</RemoveButton>
			</Wrapper>
		);
	},
);

export default TicketTypeItem;

const REMOVE_BUTTON_SELECTOR =
	'[data-component-selector="portal-settings-request-groups.portal-group.ticket-type-list.ticket-type-item.remove-button"]';

const truncatableTitleStyleCompiledStyles = css({
	flexGrow: 1,
	whiteSpace: 'nowrap',
	overflow: 'hidden',
	textOverflow: 'ellipsis',
});

const centerAlignedStyleCompiledStyles = css({
	display: 'flex',
	alignItems: 'center',
});

const ticketTypeItemWrapperSelectorValue = 'ticket-type-item-wrapper-selector';
export const ticketTypeItemWrapperSelector = `[data-component-selector="${ticketTypeItemWrapperSelectorValue}"]`;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const Wrapper = (props: any) => (
	<div
		{...props}
		data-component-selector={ticketTypeItemWrapperSelectorValue}
		css={wrapperStyles}
	/>
);

const iconWrapperStyles = xcss({
	marginLeft: 'space.050',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
const Title = styled.div(truncatableTitleStyleCompiledStyles, {
	marginLeft: token('space.100', '8px'),
	font: token('font.body'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
const DragArea = styled(DragProxy)(centerAlignedStyleCompiledStyles, {
	flexGrow: 1,
	flexShrink: 1,
	overflow: 'hidden',
	alignSelf: 'stretch',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const RemoveButton = styled(ButtonProxy)({
	transition: 'opacity 0.5s',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	width: `${gridSize * 5}px`,
	opacity: 0,
	zIndex: 0,
	'&:hover, &:focus': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		color: token('color.text.danger', colors.R300),
		opacity: 1,
		'&::after': {
			content: "''",
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
			backgroundColor: token('color.background.danger', colors.R50),
			position: 'absolute',
			borderRadius: '50%',
			zIndex: -1,
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
			height: `${gridSize * 4}px`,
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
			width: `${gridSize * 4}px`,
		},
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const WrapperOld = styled.div({
	display: 'flex',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	height: `${gridSize * 5}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	backgroundColor: token('elevation.surface', colors.N0),
	'&:hover': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		[`${REMOVE_BUTTON_SELECTOR}`]: {
			opacity: 1,
		},
	},
});

const wrapperStyles = css({
	display: 'flex',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	height: `${gridSize * 5}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	backgroundColor: token('elevation.surface', colors.N0),
	'&:hover': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		[REMOVE_BUTTON_SELECTOR]: {
			opacity: 1,
		},
	},
});
